import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, Cake, ScrollText } from "lucide-react";
import { calFetch, localYmd } from "@/lib/calApi";
import { useCalAuth } from "@/lib/calAuth";
import { CalLayout } from "@/components/calendar/CalLayout";
import { EventRow, TodayEventCard } from "@/components/calendar/EventCard";
import { CalPushCard } from "@/components/calendar/CalPushCard";
import { UpcomingList } from "@/components/calendar/UpcomingList";

const FAMILY = ["cumple", "aniversario", "especial", "yahrzeit"];

function DateHero({ today, me, myBirthday }) {
  const name = me?.nickname || me?.name?.split(" ")[0] || "";
  return (
    <section data-testid="cal-date-hero" className="cal-rise relative overflow-hidden rounded-[28px] bg-night cal-grain text-white p-6 sm:p-8 mb-6">
      <div className="absolute inset-0 cal-stars pointer-events-none" />
      <div className="absolute -bottom-20 -right-10 w-60 h-60 rounded-full bg-candle/20 blur-3xl cal-glow pointer-events-none" />
      <div className="relative">
        <p className="text-[11px] uppercase tracking-[0.25em] text-candle/85 mb-2">{myBirthday ? `¡Feliz cumpleaños, ${name}!` : `Hola, ${name}`}</p>
        <h2 data-testid="cal-greg-label" className="font-display italic text-4xl sm:text-5xl leading-[1.05] first-letter:uppercase">{today.greg_label}</h2>
        <p data-testid="cal-hebrew-label" className="font-display text-2xl text-candle mt-2">{today.hebrew_label}</p>
        {today.parasha && (
          <p data-testid="cal-parasha" className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-white/10 px-3 py-1 text-xs">
            <ScrollText className="w-3.5 h-3.5 text-candle" /> Parashá de la semana: {today.parasha}
          </p>
        )}
      </div>
    </section>
  );
}

function Section({ title, icon: Icon, children, testId }) {
  return (
    <section data-testid={testId} className="mb-8">
      <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-deep font-semibold mb-3">
        {Icon && <Icon className="w-4 h-4" />} {title}
      </h3>
      {children}
    </section>
  );
}

export default function CalHoy() {
  const { person, expire } = useCalAuth();
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const fecha = /^\d{4}-\d{2}-\d{2}$/.test(params.get("fecha") || "") ? params.get("fecha") : null;

  const load = useCallback(() => {
    setData(null);
    calFetch(`/cal/today?date=${fecha || localYmd()}`).then(setData).catch((e) => {
      if (e.status === 401) expire();
      toast.error(e.message);
    });
  }, [expire, fecha]);
  useEffect(load, [load]);

  if (!data) return <CalLayout><div className="h-64 rounded-[28px] bg-night/10 animate-pulse" /></CalLayout>;
  const { today, upcoming, me } = data;
  const family = today.events.filter((e) => FAMILY.includes(e.kind));
  const fiestas = today.events.filter((e) => e.kind === "fiesta");
  const history = today.events.filter((e) => e.kind === "efemeride" || e.kind === "historia");
  const myBirthday = family.some((e) => e.kind === "cumple" && e.person_id === (me?.id || person?.id));

  return (
    <CalLayout>
      {fecha && fecha !== localYmd() && (
        <Link to="/calendario" data-testid="cal-back-to-today" className="block mb-3 text-center text-xs rounded-full bg-candle/25 text-cocoa py-2">
          Estás viendo otro día · Volver a hoy
        </Link>
      )}
      <DateHero today={today} me={me || person} myBirthday={myBirthday} />
      <Section title="Hoy en la familia" icon={Cake} testId="cal-today-family">
        {family.length ? (
          <div className="space-y-4">{family.map((ev, i) => <TodayEventCard key={`${ev.kind}-${ev.calendar}-${ev.person_id || ev.event_id}`} ev={ev} me={me || person} index={i} />)}</div>
        ) : (
          <div data-testid="cal-today-empty" className="rounded-3xl border border-dashed border-line bg-cream/60 p-6">
            <p className="font-display italic text-cocoa text-2xl">Hoy no hay cumpleaños en la familia.</p>
            {upcoming.find((u) => u.kind === "cumple") && (
              <p className="font-serif text-cocoa-soft mt-1">
                El próximo: {upcoming.find((u) => u.kind === "cumple").title} {(() => { const n = upcoming.find((u) => u.kind === "cumple").days_until; return n === 1 ? "mañana" : `en ${n} días`; })()}.
              </p>
            )}
          </div>
        )}
      </Section>
      {fiestas.length > 0 && (
        <Section title="Calendario judío de hoy" testId="cal-today-fiestas">
          <div className="rounded-3xl bg-cream border border-line px-4 divide-y divide-line/70">
            {fiestas.map((ev, i) => <EventRow key={i} ev={ev} testId={`cal-fiesta-${i}`} />)}
          </div>
        </Section>
      )}
      <div className="mb-8"><CalPushCard compact /></div>
      {history.length > 0 && (
        <Section title="Un día como hoy" icon={BookOpen} testId="cal-today-history">
          <div className="rounded-3xl bg-cream border border-line px-4 divide-y divide-line/70">
            {history.map((ev, i) => <EventRow key={i} ev={ev} testId={`cal-history-${i}`} />)}
          </div>
        </Section>
      )}
      <Section title="Lo que viene" testId="cal-upcoming">
        <UpcomingList items={upcoming} />
      </Section>
    </CalLayout>
  );
}
